import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import { HiChevronRight } from "react-icons/hi";
import { AiOutlineClose } from "react-icons/ai";
import moment from "~/services/moment";

// UI Components
import {
  Col,
  Row,
  UiBox,
  UiButton,
  UiCenter,
  UiDivider,
  UiFlex,
  UiIcon,
  UiImage,
  UiModal,
  UiModalBody,
  UiModalContent,
  UiModalOverlay,
  UiPrimaryButton,
  UiSkeletonText,
  UiStack,
  UiTab,
  UiTabIndicator,
  UiTabList,
  UiTabPanels,
  UiTabs,
  UiText,
  useDisclosure,
  useMediaQuery,
} from "~/components/elements";

// Layout and Page Components
import { PageLayout } from "~/components/layouts/PageLayout";
import { AllHeadshotsTabPanel } from "~/pages/result-detail/AllHeadshotsTabPanel";
import { FavoriteHeadshotsTabPanel } from "~/pages/result-detail/FavoriteHeadshotsTabPanel";
import { CreateMoreHeadshotsTabPanel } from "~/pages/result-detail/CreateMoreHeadshotsTabPanel";
import { EarnMoneyTabPanel } from "~/pages/result-detail/EarnMoneyTabPanel";
import { AllAccessTabPanel } from "~/pages/result-detail/AllAccessTabPanel";

// Hooks and Redux
import { useAppDispatch, useAppSelector, useRequestState } from "~/hooks";
import {
  clearAllHeadshotStyleResultsFilter,
  clearResultDetailData,
  getCountResultImages,
  getHeadshotsFavoriteTab,
  getSelectingHeadshotStyles,
  selectHeadshotStyle,
  setShowGoToFavPopup,
} from "~/redux/dashboards/slice";
import { fetchJobData, fetchOutfits } from "~/redux/dashboards/thunk";

// Utils and Constants
import { fetchBinaryContent, LONG_DATE_FORMAT } from "~/utils/helpers";
import { PAGES, QUERY_MOBILE } from "~/constants";

// Assets
import starFace from "~/assets/img/star_face.png";
import referMoneyBag from "~/assets/img/refer_money_bag.png";
import { StyleListItemForDetail } from "~/pages/result-detail/StyleListItemForDetail";

export default function ResultDetailPage() {
  // Router and URL params
  const history = useHistory();
  const params: { id: string } = useParams();
  const jobId = params.id;

  // Redux setup
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(state => state.auth.currentUser);
  const jobItemData = useAppSelector(state => state.dashboard.jobItemData);
  const headshotStyleResults = useAppSelector(state => state.dashboard.headshotStyleResults);
  const selectAllCheckboxValue = useAppSelector(state => state.dashboard.selectAllCheckboxValue);
  const selectAllFavoriteCheckboxValue = useAppSelector(state => state.dashboard.selectAllFavoriteCheckboxValue);
  const selectingHeadshotStyles = useAppSelector(getSelectingHeadshotStyles);
  const headshotsCount = useAppSelector(getCountResultImages);
  const resultHeadshotsGroupByFavorite = useAppSelector(state => getHeadshotsFavoriteTab(state, { styleIds: [] }));
  const { loading } = useRequestState(fetchJobData);
  const location = useLocation();
  // @ts-ignore
  const tabNameSelected = location.state?.tabName as string;
  // UI state and hooks
  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`, { ssr: false });
  const [tabIndex, setTabIndex] = useState(0);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const zip = new JSZip();

  // Tab configuration
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tabsMap = [
    { name: "tab_all", isHidden: false, title: `All photoshoots (${headshotsCount.all ? headshotsCount.all : "120"})` },
    { name: "tab_favorite", isHidden: false, title: `Favorite photoshoots (${headshotsCount.favourite})` },
    { name: "tab_upsell", isHidden: false, title: "Get more styles" },
    { name: "tab_refer", isHidden: false, title: "Earn money" },
    // {
    //   name: "tab_subscription",
    //   isHidden: currentUser?.is_all_access_plan,
    //   title: (
    //     <Row align="center">
    //       <UiText vr="title2" className="gradient-text" noOfLines={1}>
    //         ALL ACCESS&nbsp;
    //       </UiText>
    //       <UiText as="span" fontSize="11px" mb="2px">
    //         ⭐
    //       </UiText>
    //     </Row>
    //   ),
    // },
  ];

  // Effects
  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchJobData({ id: jobId, source: "RESULT_PAGE" }));
    };
    if (jobId) {
      fetchData();
      dispatch(fetchOutfits({}));
    }

    return () => {
      dispatch(clearResultDetailData({}));
    };
  }, [dispatch, jobId]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!isDesktop) {
      const storageKey = `modal_shown_${jobId}`;
      const lastShown = localStorage.getItem(storageKey);
      const currentTime = new Date().getTime();

      // 1 day
      if (!lastShown || currentTime - parseInt(lastShown, 10) > 24 * 60 * 60 * 1000) {
        const timer = setTimeout(() => {
          onOpen();
          localStorage.setItem(storageKey, currentTime.toString());
        }, 3000);

        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, jobId]);

  const goToTab = useCallback(
    (tabName: string) => {
      const tabIdx = tabsMap.filter(t => !t.isHidden).findIndex(tab => tab.name === tabName);
      setTabIndex(tabIdx);
    },
    [tabsMap]
  );

  const onChangeTab = (idx: number) => {
    removeAllSelecting();
    dispatch(setShowGoToFavPopup({ isShow: false }));
    dispatch(clearAllHeadshotStyleResultsFilter());
    setTabIndex(idx);
  };

  const removeAllSelecting = () => {
    // dispatch(selectImagesByGroup({ group: "all", isSelected: false }));
    // dispatch(setSelectAllCheckboxes({ group: "all_groups", isSelected: false }));
  };

  const onFilterByStyle = (hss: any) => {
    // removeAllSelecting();
    dispatch(selectHeadshotStyle({ headshotStyle: hss, isSelected: !hss.is_selected }));
  };

  const onClickShare = () => {
    if (!headshotsCount.favourite) {
      return;
    }

    if (jobItemData?.slug) {
      history.push(`${PAGES.SHARED}/${jobItemData.slug}`);
    }
  };

  const handleDownloadImages = async () => {
    if (!headshotsCount.favourite) {
      return;
    }
    setDownloadLoading(true);
    resultHeadshotsGroupByFavorite.favorited?.map(image =>
      // @ts-ignore
      zip.file(`images/${image.id}.png`, fetchBinaryContent(image.image_url), { base64: true })
    );

    await zip.generateAsync({ type: "blob" }).then(blob => {
      saveAs(blob, `bettershot-${moment().format("YYYYMMDDmmss")}.zip`);
    });
    setDownloadLoading(false);
  };
  useEffect(() => {
    if (tabNameSelected) {
      goToTab(tabNameSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderMoreHeadshotActionBtn = () => (
    <UiCenter rounded="xl" bg="lightblue" flexDirection="column" p={4}>
      <UiImage src={starFace} h="2.25rem" objectFit="cover" />
      <UiText textAlign="center" vr="title1" noOfLines={1} mt={2}>
        Loved your photoshoot?
      </UiText>
      <UiText textAlign="center" vr="title1">
        {jobItemData?.type === "two_person"
          ? "Get more styles for just"
          : "Create more with any outfit and background combination for"}
        &nbsp;
        <UiText as="span" vr="title1" color="blue">
          $3 each
        </UiText>
      </UiText>
      <UiPrimaryButton
        mt={4}
        w="full"
        h="2.5rem"
        maxW={{ base: undefined, md: "16.25rem" }}
        variant="outline"
        borderColor="darkblue"
        bg="transparent"
        color="darkblue"
        _hover={{
          bg: "transparent",
          color: "darkblue",
        }}
        onClick={() => {
          goToTab("tab_upsell");
          onClose();
        }}
        rightIcon={<HiChevronRight />}
      >
        <UiText vr="title1" color="darkblue">
          Add now
        </UiText>
      </UiPrimaryButton>
    </UiCenter>
  );
  // Main render
  return (
    <PageLayout
      metaTagsConfig={{
        metas: [{ name: "robots", content: "noindex,nofollow" }],
      }}
    >
      <UiBox>
        <UiFlex flexDirection={{ base: "column", md: "row" }}>
          {/* Left Sidebar */}
          <Col w="full" maxW={{ md: "21rem" }} px={{ base: 4, md: 5 }}>
            <Row justifyContent="space-between" align="center" mt={5}>
              <UiText vr="headline4">Your Photoshoots</UiText>
              <UiText vr="body1" color="neutral.gray500">
                {jobItemData?.completed_at ? moment(jobItemData.completed_at).format(LONG_DATE_FORMAT) : "_"}
              </UiText>
            </Row>
            {isDesktop && <UiDivider mt={6} />}

            {/* Photoshoot Styles Section */}
            <Row mt={6} align="center" justify="space-between">
              <UiText vr="title1">Your photoshoot styles ({headshotStyleResults?.length})</UiText>
              {selectingHeadshotStyles?.length > 0 && (
                <UiButton
                  minH={0}
                  h="auto"
                  variant="unstyle"
                  px={0}
                  leftIcon={<UiIcon as={AiOutlineClose} color="blue" boxSize={4} mx={0} />}
                  onClick={() => {
                    dispatch(clearAllHeadshotStyleResultsFilter());
                    removeAllSelecting();
                  }}
                >
                  <UiText vr="body2" color="blue" ml={-1}>
                    Clear
                  </UiText>
                </UiButton>
              )}
            </Row>
            <UiSkeletonText noOfLines={{ base: 3, md: 10 }} skeletonHeight={5} isLoaded={!loading} mt={4}>
              <UiStack direction={{ base: "row", md: "column" }} overflow="auto" pb={2}>
                {headshotStyleResults?.map((headshotStyle, idx) => (
                  <StyleListItemForDetail
                    key={idx}
                    _hover={{ cursor: tabIndex === 0 ? "pointer" : "not-allowed" }}
                    headshotStyle={headshotStyle}
                    photoshootType={jobItemData?.type}
                    filterable={tabIndex === 0}
                    onSelect={onFilterByStyle}
                  />
                  // <StyleListItem
                  //   key={idx}
                  //   _hover={{ cursor: tabIndex === 0 ? "pointer" : "not-allowed" }}
                  //   headshotStyle={headshotStyle}
                  //   filterable={tabIndex === 0}
                  //   onSelect={onFilterByStyle}
                  // />
                ))}
              </UiStack>
            </UiSkeletonText>

            {/* Refer & Earn Section @TODO tmp hide */}
            <Row
              hideBelow="sm"
              rounded="xl"
              bg="white"
              p={2}
              w="full"
              minW="14.9rem"
              _hover={{ cursor: "pointer" }}
              onClick={() => {
                goToTab("tab_refer");
              }}
              mb={{ base: 4, md: 20 }}
            >
              <Col mr={2} w="4rem" minW="4rem" align="center" justify="center">
                <UiImage src={referMoneyBag} h="2.625rem" objectFit="cover" />
              </Col>
              <Col flexGrow={1} justifyContent="space-between">
                <UiText vr="title2" noOfLines={1} color="green">
                  Refer & Earn Money
                </UiText>
                <UiText vr="body2" color="neutral.gray400" noOfLines={3} mt={1}>
                  Earn 50% commission for customers you refer
                </UiText>
              </Col>
            </Row>

            {/* More Photoshoots Action (Desktop) */}
            {isDesktop && renderMoreHeadshotActionBtn()}
          </Col>

          {/* Mobile Divider */}
          {!isDesktop && (
            <Col px={4} mt={4}>
              <UiDivider />
            </Col>
          )}

          {/* Main Content Area */}
          <Col w="full" flexGrow={1} borderLeftWidth={{ base: 0, md: "1px" }} borderColor="#0B0D2B1A">
            <UiTabs mt={4} index={tabIndex} variant="unstyled" onChange={onChangeTab} position="relative">
              <UiBox overflowX="auto" whiteSpace="nowrap" sx={{ "&::-webkit-scrollbar": { display: "none" } }}>
                <UiTabList minW="max-content" position="relative" px={4} gap={4}>
                  {tabsMap.map((tab, idx) => {
                    if (tab.isHidden) {
                      return null;
                    }
                    return (
                      <UiTab
                        key={idx}
                        borderBottom={!isDesktop ? "2px solid transparent" : "none"}
                        flexShrink={0}
                        _selected={{ color: "blue", borderBottom: isDesktop ? "none" : "2px solid blue" }}
                        color="neutral.gray400"
                        px={0}
                      >
                        <UiText vr="title2" color="inherit" noOfLines={1}>
                          {tab.title}
                        </UiText>
                      </UiTab>
                    );
                  })}
                </UiTabList>
              </UiBox>
              {isDesktop && <UiTabIndicator mt="-2px" height="2px" bg="blue" />}

              {/* Tab Panels */}
              <UiTabPanels>
                <AllHeadshotsTabPanel
                  jobId={jobId}
                  loading={!!loading}
                  selectAllCheckboxValue={selectAllCheckboxValue}
                  headshotsCount={headshotsCount}
                  downloadLoading={downloadLoading}
                  goToTab={goToTab}
                  onHandleDownloadImages={handleDownloadImages}
                  onClickShare={onClickShare}
                />
                <FavoriteHeadshotsTabPanel
                  selectAllFavoriteCheckboxValue={selectAllFavoriteCheckboxValue}
                  headshotsCount={headshotsCount}
                  downloadLoading={downloadLoading}
                  onHandleDownloadImages={handleDownloadImages}
                  onClickShare={onClickShare}
                />
                <CreateMoreHeadshotsTabPanel jobId={jobId} />
                <EarnMoneyTabPanel />
                {!currentUser?.is_all_access_plan && <AllAccessTabPanel jobId={jobId} />}
              </UiTabPanels>
            </UiTabs>
          </Col>
        </UiFlex>
      </UiBox>

      {/* Modal */}
      <UiModal isOpen={isOpen} onClose={onClose} isCentered size="xs">
        <UiModalOverlay />
        <UiModalContent>
          <UiModalBody p={0}>{renderMoreHeadshotActionBtn()}</UiModalBody>
        </UiModalContent>
      </UiModal>
    </PageLayout>
  );
}
